import _ from "lodash";
import axios from "@util/axios";
import store from "@store";

class ReportsService {
	constructor() {
		this.user = store.getters.user;

		this.report = store.getters.report;
		this.reports = store.getters.reports;

		store.subscribe((mutation, state) => {
			this.user = state.auth.user;

			this.report = state.reports.report;
			this.reports = state.reports.reports;
		});
	}

	set(type = "report", data) {
		if ("list" === type)
			store.commit(
				"GET_REPORTS",
				data
					? {
							count: data.length,
							results: data,
					  }
					: null
			);
		else
			store.commit(
				data && data.id === this.report.id
					? "UPDATE_REPORT"
					: "READ_REPORT",
				data
					? {
							data: data,
					  }
					: null
			);
	}

	async list(params, commit = true) {
		var id =
				"Company" == this.user.type
					? this.user.company
						? this.user.company.id
						: null
					: this.user.member_id,
			type = "Company" == this.user.type ? "Corporate Admin" : "Employee";

		if (this.user.is_superuser) {
			id = this.user.id;
			type = "superuser";
		}

		if (params) {
			if ((params.company_id || params.member_id) && params.assignee) {
				id = params.company_id || params.member_id;
				type = params.assignee;
			}
		}

		if (!id || !type) return false;

		const response = await axios({
			url:
				params && params.refresh
					? `/reports/refresh/?refresh=True`
					: `/assigned/report/${id}/${type}`,
			params: _.pick(params, ["page", "page_size"]),
		});

		if (commit) store.commit("GET_REPORTS", response.data.data);

		return response;
	}

	async read(data, params = null, commit = true) {
		const response = await axios({
			url: `/get/report/${data.id}`,
			params: params,
		});

		if (commit) {
			store.commit("READ_REPORT", response.data.data);
			store.commit("VALIDATE_REPORT", true);
		}

		return response;
	}

	async update(data, commit = true) {
		const response = await axios({
			url: `/reports/update/${data.id}/`,
			method: "PUT",
			data: _.pick(data, ["display_name", "exportable"]),
		});

		if (commit) {
			store.commit("UPDATE_REPORT", response.data.data);

			if (data.id == response.data.data.data.id)
				store.commit("READ_REPORT", response.data.data);
		}

		return response;
	}

	async delete(data, commit = true) {
		const response = await axios({
			url:
				data.assignee && "Employee" == data.assignee
					? `/reports/unassigned/member/${data.id}`
					: `/reports/unassigned/${data.id}`,
			method: "DELETE",
		});

		if (commit)
			store.commit("DELETE_REPORT", {
				data: data,
			});

		return response;
	}

	async share(data) {
		const response = await axios({
			url: `/auth/share/report/`,
			method: "POST",
			data: data,
		});

		return response;
	}

	async assignAccess(data, forMember = true, commit = true) {
		const response = await axios({
			url: forMember ? `/reports/assign/member/` : `/reports/assign/`,
			method: "POST",
			data: _.pick(data, [
				"report_list",
				"member_list",
				"company_list",
				"company_id",
			]),
		});

		if (commit)
			store.commit(
				"ASSIGN_REPORT_ACCESS",
				response.data.data.data.map((item) => {
					return {
						id: item.id,
						report_id: item.report.id,
						name: item.report.name,
					};
				})
			);

		return response;
	}

	async unassignAccess(data, forMember = true, commit = true) {
		const response = await axios({
			url: `/reports/unassigned${forMember ? "/member/" : "/"}${data.id}`,
			method: "DELETE",
		});

		if (commit)
			store.commit("UNASSIGN_REPORT_ACCESS", {
				data: data,
			});

		return response;
	}

	async favorite(data, action = "remove", commit = true) {
		const response = await axios({
			url:
				"remove" === action
					? `/reports/remove/favorite/${data.user_id}/${data.report_id}/`
					: `/reports/set/favorite/${data.user_id}/`,
			method: "remove" === action ? "DELETE" : "POST",
			data: _.pick(data, ["report_id"]),
		});

		if (commit) store.commit("FAVORITE_REPORT", action);

		return response;
	}

	async getExportParameters(parameters = {}) {
		const rs = await axios({
			url: `/get/parameters/`,
			params: parameters,
		});

		return rs;
	}

	async getExportParametersV2(data = {}) {
		let url = '/dedupe/get/parameters/';
		return await axios({
			url: url,
			method: 'POST',
			data: data
		});
	}

	async exportData(payload) {
		let url = this.getExportReportUrl();
		const rs = await axios({
			url: `${url}/`,
			method: "POST",
			data: _.omitBy(payload, _.isNull),
		});

		return rs;
	}

	/**
	 * LIQ-2190 — data for the Cash Call Reconciliation wizard step. Takes the
	 * same filter payload the export uses and returns the claim rows that match
	 * a LimeIQ cash call on Claim Reference + LimeIQ Key, each with its
	 * auto-suggested Cash Call Action and derived figures.
	 */
	async scanCashCallReconciliation(payload) {
		const rs = await axios({
			url: "/reports/export/cash-call-reconciliation/",
			method: "POST",
			data: payload || {},
		});
		// The viewset wraps its payload as {status, data:{rows,...}} and the
		// response itself is wrapped again, so the rows sit two levels deep.
		// Unwrap defensively rather than assuming a fixed depth.
		const empty = { rows: [], has_matches: false };
		let body = rs && rs.data ? rs.data : null;
		while (body && !body.rows && body.data) body = body.data;
		return body && body.rows ? body : empty;
	}

	async exportReport(reportId, parameters, exportBy = null, powerbiRoleVal) {
		let url = this.getExportReportUrl();
		const rs = await axios({
			url: `/${url}/${reportId}/`,
			method: "POST",
			data: _.omitBy(
				{
					parameters: parameters,
					powerbi_role_value:
						powerbiRoleVal ||
						process.env.VUE_APP_SUPERUSER_ROLE_VALUE,
					export_by: exportBy,
				},
				_.isNull
			),
		});

		return rs;
	}

	async assignTags(reportId, tags) {
		const rs = await axios({
			url: `/report/assign-unassign/tags/${reportId}`,
			method: 'PUT',
			data: {
				tags: tags 
			}
		});

		return rs;
	}

	/**
	 * Gets the appropiate save report url based on the page pathname.
	 * @return string
	 */
	getExportReportUrl() {
		let url = '/dedupe/reports/export'; 
		return url;

	}
}

export default new ReportsService();
